import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTheme } from "./ThemeContext";

export default function LandingPage() {
  const navigate = useNavigate();
  const { toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ink:       #0f1117;
    --ink-soft:  #4a4f5e;
    --ink-faint: #8b91a1;
    --bg:        #f7f8fa;
    --surface:   #ffffff;
    --border:    #e4e6ed;
    --accent:    #1a56db;
    --accent-lt: #e8effe;
    --ai:        #0f766e;
    --cite:      #7c3aed;
    --data:      #b45309;
    --pm:        #be123c;
    --eval:      #059669;
    --radius:    14px;
    --shadow:    0 2px 12px rgba(15,17,23,.07), 0 1px 3px rgba(15,17,23,.05);
    --shadow-lg: 0 8px 40px rgba(15,17,23,.11), 0 2px 8px rgba(15,17,23,.06);
    --grad-primary: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
    --grad-accent: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
    --grad-bright: linear-gradient(135deg, #667eea 0%, #764ba2 35%, #00d4ff 100%);
    --grad-warm: linear-gradient(135deg, #764ba2 0%, #f093fb 100%);
  }

  [data-theme="dark"] {
    --ink:       #f7f8fa;
    --ink-soft:  #cbd5e1;
    --ink-faint: #94a3b8;
    --bg:        #0f1117;
    --surface:   #1a1f3a;
    --border:    #2d3748;
    --accent:    #60a5fa;
    --accent-lt: #1e3a8a;
  }

  html { scroll-behavior: smooth; }

  body {
    font-family: 'DM Sans', sans-serif;
    background: var(--bg);
    color: var(--ink);
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
  }

  nav {
    position: sticky; top: 0; z-index: 100;
    background: rgba(247,248,250,.88);
    backdrop-filter: blur(14px);
    border-bottom: 1px solid var(--border);
    padding: 0 clamp(1.5rem, 5vw, 4rem);
    display: flex; align-items: center; justify-content: space-between;
    height: 64px;
  }
  [data-theme="dark"] nav {
    background: rgba(15,17,23,.88);
  }
  .nav-brand {
    display: flex; align-items: center; gap: 1.2rem;
    font-family: 'DM Serif Display', serif;
    font-size: 1.2rem; color: var(--ink); text-decoration: none;
  }
  .nav-brand .dot { color: var(--accent); }
  .nav-badge {
    font-family: 'DM Sans', sans-serif;
    font-size: .68rem; font-weight: 600; letter-spacing: .06em;
    text-transform: uppercase; color: var(--accent);
    background: var(--accent-lt); padding: .2rem .55rem;
    border-radius: 99px;
  }
  .nav-links { display: flex; gap: 2rem; list-style: none; }
  .nav-links a {
    font-size: .88rem; font-weight: 500;
    color: var(--ink-soft); text-decoration: none;
    transition: color .2s;
  }
  .nav-links a:hover { color: var(--ink); }
  .nav-right { display: flex; align-items: center; gap: 1.2rem; }
  .nav-theme-toggle {
    background: transparent; border: none; cursor: pointer;
    font-size: 1.2rem; color: var(--ink);
    padding: .4rem; border-radius: 6px;
    transition: background .2s;
    display: flex; align-items: center; justify-content: center;
  }
  .nav-theme-toggle:hover { background: var(--accent-lt); }
  .nav-cta {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #fff;
    padding: .5rem 1.2rem; border-radius: 8px;
    font-size: .88rem; font-weight: 600; text-decoration: none;
    transition: all .2s; display: inline-block;
    box-shadow: 0 4px 12px rgba(102,126,234,.3);
  }
  .nav-cta:hover { 
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(102,126,234,.4);
  }
  
  .nav-hamburger {
    display: none;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 1.5rem;
    color: var(--ink);
    padding: .4rem;
  }
  
  .mobile-menu {
    position: fixed;
    top: 64px;
    left: 0;
    right: 0;
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    padding: 1rem 2rem;
    display: none;
    flex-direction: column;
    gap: 1rem;
    z-index: 99;
    box-shadow: 0 4px 12px rgba(0,0,0,.1);
  }
  
  .mobile-menu.open {
    display: flex;
  }
  
  .mobile-menu a {
    color: var(--ink-soft);
    text-decoration: none;
    font-size: .95rem;
    font-weight: 500;
    transition: color .2s;
  }
  
  .mobile-menu a:hover {
    color: var(--ink);
  }
  
  @media (max-width: 768px) {
    .nav-hamburger {
      display: block;
    }
    
    .nav-links {
      display: none;
    }
    
    .nav-cta {
      display: none;
    }
    
    nav {
      padding: 0 1rem;
    }
  }

  .hero {
    padding: clamp(4rem, 10vw, 8rem) clamp(1.5rem, 5vw, 4rem) 4rem;
    max-width: 860px; margin: 0 auto; text-align: center;
    animation: fadeUp .7s ease both;
    background: linear-gradient(180deg, rgba(102,126,234,.06) 0%, rgba(118,75,162,.04) 100%);
  }
  .hero-eyebrow {
    display: inline-flex; align-items: center; gap: .5rem;
    font-size: .8rem; font-weight: 600; letter-spacing: .08em;
    text-transform: uppercase; color: #667eea;
    background: linear-gradient(135deg, rgba(102,126,234,.1) 0%, rgba(118,75,162,.08) 100%);
    padding: .35rem .9rem;
    border-radius: 99px; margin-bottom: 1.6rem;
    border: 1px solid rgba(102,126,234,.2);
  }
  .hero-eyebrow::before {
    content: ''; width: 6px; height: 6px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 50%;
  }
  h1 {
    font-family: 'DM Serif Display', serif;
    font-size: clamp(2.4rem, 6vw, 4rem);
    line-height: 1.15; color: var(--ink);
    margin-bottom: 1.2rem;
  }
  h1 em { 
    font-style: italic; 
    background: var(--grad-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .hero p {
    font-size: 1.15rem; color: var(--ink-soft);
    max-width: 580px; margin: 0 auto 2.4rem;
    font-weight: 300;
  }
  .hero-actions { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }
  .btn-primary {
    background: var(--grad-primary); color: #fff;
    padding: .8rem 2rem; border-radius: 10px;
    font-size: .95rem; font-weight: 600; text-decoration: none;
    box-shadow: 0 4px 24px rgba(102,126,234,.4);
    transition: transform .18s, box-shadow .18s, filter .18s;
    display: inline-block;
  }
  .btn-primary:hover { 
    transform: translateY(-3px); 
    box-shadow: 0 12px 36px rgba(102,126,234,.5);
    filter: brightness(1.08);
  }
  .btn-ghost {
    background: transparent; color: var(--ink);
    padding: .8rem 1.8rem; border-radius: 10px;
    font-size: .95rem; font-weight: 500; text-decoration: none;
    border: 2px solid transparent;
    background-clip: padding-box;
    transition: all .18s;
    display: inline-block;
    position: relative;
  }
  .btn-ghost::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    border-radius: 10px;
    padding: 2px;
    background: var(--grad-bright);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    z-index: -1;
  }
  .btn-ghost:hover { background: rgba(102,126,234,.08); }

  .stats {
    display: flex; justify-content: center; gap: 0;
    flex-wrap: wrap;
    border-top: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
    background: var(--surface);
    animation: fadeUp .7s .15s ease both;
  }
  .stat {
    flex: 1; min-width: 160px;
    padding: 1.6rem 2rem; text-align: center;
    border-right: 1px solid var(--border);
  }
  .stat:last-child { border-right: none; }
  .stat-num {
    font-family: 'DM Serif Display', serif;
    font-size: 2rem; color: var(--ink); display: block;
  }
  .stat-label { font-size: .82rem; color: var(--ink-faint); font-weight: 500; }

  .section { padding: 5rem clamp(1.5rem, 5vw, 4rem); max-width: 1180px; margin: 0 auto; }
  .section-header { margin-bottom: 2.8rem; }
  .section-header h2 {
    font-family: 'DM Serif Display', serif;
    font-size: clamp(1.6rem, 3.5vw, 2.4rem); margin-bottom: .5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .section-header p { color: var(--ink-soft); font-size: 1rem; }

  .category-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 1.4rem;
  }
  .cat-card {
    background: var(--surface); border: 1px solid var(--border);
    border-radius: var(--radius); padding: 1.8rem;
    text-decoration: none; color: inherit;
    box-shadow: var(--shadow);
    transition: all .22s cubic-bezier(.34, 1.56, .64, 1);
    display: flex; flex-direction: column; gap: .9rem;
    animation: fadeUp .6s ease both;
    position: relative;
    overflow: hidden;
  }
  .cat-card::before {
    content: '';
    position: absolute;
    top: 0; right: 0;
    width: 200px; height: 200px;
    background: linear-gradient(135deg, rgba(102,126,234,.15) 0%, rgba(240,147,251,.1) 100%);
    border-radius: 50%;
    opacity: 0;
    transition: opacity .22s;
    z-index: -1;
  }
  .cat-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 48px rgba(102,126,234,.25);
  }
  .cat-card:hover::before { opacity: 1; }
  .cat-card:nth-child(1) { animation-delay: .05s; }
  .cat-card:nth-child(2) { animation-delay: .12s; }
  .cat-card:nth-child(3) { animation-delay: .19s; }
  .cat-card:nth-child(4) { animation-delay: .26s; }
  .cat-card:nth-child(5) { animation-delay: .33s; }

  .cat-icon {
    width: 48px; height: 48px; border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-size: 1.4rem;
  }
  .cat-card[data-cat="ai"] .cat-icon { background: #d1faf5; }
  .cat-card[data-cat="cite"] .cat-icon { background: #ede9fe; }
  .cat-card[data-cat="data"] .cat-icon { background: #fef3c7; }
  .cat-card[data-cat="pm"] .cat-icon { background: #ffe4e6; }
  .cat-card[data-cat="eval"] .cat-icon { background: #d1fae5; }

  .cat-card[data-cat="ai"]:hover   { border-color: var(--ai); }
  .cat-card[data-cat="cite"]:hover { border-color: var(--cite); }
  .cat-card[data-cat="data"]:hover { border-color: var(--data); }
  .cat-card[data-cat="pm"]:hover   { border-color: var(--pm); }
  .cat-card[data-cat="eval"]:hover  { border-color: var(--eval); }

  .cat-card h3 { font-size: 1.05rem; font-weight: 600; }
  .cat-card p  { font-size: .88rem; color: var(--ink-soft); line-height: 1.55; flex: 1; }
  .cat-meta {
    display: flex; align-items: center; justify-content: space-between;
    font-size: .8rem; color: var(--ink-faint); font-weight: 500;
  }
  .cat-tag {
    padding: .2rem .6rem; border-radius: 99px;
    font-size: .72rem; font-weight: 600; letter-spacing: .04em;
  }
  .cat-card[data-cat="ai"]   .cat-tag { background: #d1faf5; color: var(--ai); }
  .cat-card[data-cat="cite"] .cat-tag { background: #ede9fe; color: var(--cite); }
  .cat-card[data-cat="data"] .cat-tag { background: #fef3c7; color: var(--data); }
  .cat-card[data-cat="pm"]   .cat-tag { background: #ffe4e6; color: var(--pm); }
  .cat-card[data-cat="eval"] .cat-tag { background: #d1fae5; color: var(--eval); }

  .tutorial-list { display: flex; flex-direction: column; gap: 1rem; }
  .tut-row {
    background: var(--surface); border: 1px solid var(--border);
    border-radius: var(--radius); padding: 1.4rem 1.8rem;
    display: flex; align-items: center; gap: 1.4rem;
    text-decoration: none; color: inherit;
    box-shadow: var(--shadow);
    transition: transform .2s, box-shadow .2s, border-color .2s;
  }
  .tut-row:hover { transform: translateX(4px); box-shadow: var(--shadow-lg); }
  .tut-dot {
    width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
  }
  .tut-row[data-cat="ai"]   .tut-dot { background: var(--ai); }
  .tut-row[data-cat="cite"] .tut-dot { background: var(--cite); }
  .tut-row[data-cat="data"] .tut-dot { background: var(--data); }
  .tut-row[data-cat="pm"]   .tut-dot { background: var(--pm); }

  .tut-body { flex: 1; }
  .tut-body h4 { font-size: .97rem; font-weight: 600; margin-bottom: .2rem; }
  .tut-body p  { font-size: .83rem; color: var(--ink-soft); }
  .tut-meta { display: flex; flex-direction: column; align-items: flex-end; gap: .3rem; flex-shrink: 0; }
  .tut-level {
    font-size: .72rem; font-weight: 600; letter-spacing: .05em;
    padding: .18rem .55rem; border-radius: 99px;
    background: var(--bg); color: var(--ink-soft);
  }
  .tut-time { font-size: .75rem; color: var(--ink-faint); }
  .tut-arrow { color: var(--ink-faint); font-size: 1rem; flex-shrink: 0; }

  .cta-banner {
    margin: 0 clamp(1.5rem, 5vw, 4rem) 5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
    border-radius: 20px;
    padding: 4rem clamp(2rem, 6vw, 5rem);
    display: flex; align-items: center; justify-content: space-between;
    gap: 2rem; flex-wrap: wrap;
    overflow: hidden; position: relative;
  }
  .cta-banner::before {
    content: '';
    position: absolute; top: -40px; right: -120px;
    width: 400px; height: 400px;
    background: radial-gradient(circle, rgba(255,255,255,.15) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }
  .cta-banner::after {
    content: '';
    position: absolute; bottom: -80px; left: -80px;
    width: 300px; height: 300px;
    background: radial-gradient(circle, rgba(240,147,251,.2) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }
  .cta-banner h2 {
    font-family: 'DM Serif Display', serif;
    font-size: clamp(1.5rem, 3vw, 2rem);
    color: #fff; max-width: 460px; line-height: 1.25;
    position: relative; z-index: 1;
  }
  .cta-banner h2 em { font-style: italic; color: #ffffff; text-shadow: 0 2px 10px rgba(0,0,0,.15); }
  .cta-banner .btn-primary { 
    background: #fff; 
    color: #667eea; 
    box-shadow: 0 8px 24px rgba(0,0,0,.2);
    position: relative; z-index: 1;
  }
  .cta-banner .btn-primary:hover { 
    background: #f5f5ff;
    transform: translateY(-2px);
  }

  footer {
    border-top: 1px solid var(--border);
    padding: 2.4rem clamp(1.5rem, 5vw, 4rem);
    display: flex; align-items: center; justify-content: space-between;
    flex-wrap: wrap; gap: 1rem;
    font-size: .82rem; color: var(--ink-faint);
  }
  footer a { color: var(--ink-soft); text-decoration: none; }
  footer a:hover { color: var(--accent); }
  .footer-links { display: flex; gap: 1.4rem; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(22px); }
    to   { opacity: 1; transform: translateY(0); }
  }

`;

  const categories = [
    { cat: "ai", icon: "🤖", name: "AI Writing & Research Tools", desc: "Learn to use Claude, ChatGPT, Perplexity, and other AI assistants to accelerate academic research and writing without compromising integrity.", count: "12 Tutorials", level: "Beginner → Advanced" },
    { cat: "cite", icon: "📚", name: "Citation & Reference Managers", desc: "Master Zotero, Mendeley, and EndNote. Build libraries, generate citations in APA, Harvard, or Vancouver, and sync across devices.", count: "10 Tutorials", level: "Beginner → Intermediate" },
    { cat: "data", icon: "📊", name: "Data Analysis & Statistics", desc: "From SPSS to Python and R, learn to clean datasets, run regressions, produce visualisations, and interpret findings for academic submission.", count: "11 Tutorials", level: "Intermediate → Advanced" },
    { cat: "pm", icon: "🗂️", name: "Project & Task Management", desc: "Use Notion, Trello, Asana, and MS Project to plan dissertations, group projects, and coursework schedules with academic rigour.", count: "9 Tutorials", level: "All Levels" },
    { cat: "eval", icon: "🎓", name: "Assignment Evaluator", desc: "AI-powered assignment evaluation using Claude AI. Get detailed feedback, rubric breakdown, strengths, weaknesses, and improvement suggestions.", count: "Interactive Tool", level: "All Levels" },
  ];

  const tutorials = [
    { cat: "ai", title: "Getting Started with Claude for Academic Research", desc: "Learn how to prompt Claude effectively for literature reviews, summaries, and structured outlines.", level: "Beginner", time: "15 min" },
    { cat: "cite", title: "Setting Up Zotero with APA 7th Edition", desc: "Install, configure, and link Zotero to Word or Google Docs — with automatic APA citation generation.", level: "Beginner", time: "20 min" },
    { cat: "data", title: "Running Descriptive Statistics in SPSS", desc: "Import data, clean variables, and produce frequency tables, means, and standard deviations for your research report.", level: "Intermediate", time: "30 min" },
    { cat: "pm", title: "Planning Your Dissertation with Notion", desc: "Build a Gantt-style timeline, chapter tracker, and reading log inside Notion to stay on schedule from proposal to submission.", level: "All Levels", time: "25 min" },
    { cat: "ai", title: "Using Perplexity AI for Source Discovery", desc: "How to use Perplexity's citation-backed search to identify relevant journal articles and grey literature fast.", level: "Beginner", time: "12 min" },
    { cat: "data", title: "Data Visualisation with Python (Matplotlib & Seaborn)", desc: "Create publication-ready charts, heatmaps, and regression plots from your research dataset using Python.", level: "Advanced", time: "45 min" },
  ];

  return (
    <>
      <style>{styles}</style>

      <nav>
        <a href="#" className="nav-brand">flycodeinv<span className="dot">.</span>com <span className="nav-badge">Tutorials</span></a>
        <ul className="nav-links">
          <li><a href="#categories">Categories</a></li>
          <li><a href="#tutorials">Tutorials</a></li>
          <li><a href="#" onClick={() => navigate('/about')}>About</a></li>
        </ul>
        <div className="nav-right">
          <button className="nav-theme-toggle" onClick={toggleTheme} title="Toggle theme">
            {'☀️'}
          </button>
          <a href="#tutorials" className="nav-cta">Browse All →</a>
          <button className="nav-hamburger" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            ☰
          </button>
        </div>
      </nav>
      
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <a href="#categories" onClick={() => setMobileMenuOpen(false)}>Categories</a>
        <a href="#tutorials" onClick={() => setMobileMenuOpen(false)}>Tutorials</a>
        <a href="#" onClick={() => { navigate('/about'); setMobileMenuOpen(false); }}>About</a>
        <a href="#" onClick={() => { navigate('/contact'); setMobileMenuOpen(false); }}>Contact</a>
      </div>

      <header className="hero">
        <div className="hero-eyebrow">Academic Tools Hub</div>
        <h1>Master the tools that power <em>modern learning</em></h1>
        <p>Step-by-step tutorials covering AI writing assistants, citation managers, data analysis platforms, and project tools — for every level of learner.</p>
        <div className="hero-actions">
          <a href="#tutorials" className="btn-primary">Start Learning</a>
          <a href="#categories" className="btn-ghost">Explore Categories</a>
        </div>
      </header>

      <div className="stats">
        <div className="stat"><span className="stat-num">40+</span><span className="stat-label">Tutorials</span></div>
        <div className="stat"><span className="stat-num">5</span><span className="stat-label">Tool Categories</span></div>
        <div className="stat"><span className="stat-num">All</span><span className="stat-label">Skill Levels</span></div>
        <div className="stat"><span className="stat-num">Free</span><span className="stat-label">Always</span></div>
      </div>

      <section className="section" id="categories">
        <div className="section-header">
          <h2>Browse by Category</h2>
          <p>Choose a tool area to explore structured tutorials and guides.</p>
        </div>
        <div className="category-grid">
          {categories.map((cat) => (
            <div 
              key={cat.cat}
              onClick={() => {
                if (cat.cat === 'eval') {
                  navigate('/assignment-evaluator-detail');
                } else {
                  navigate(`/category/${cat.cat}`);
                }
              }}
              className="cat-card" 
              data-cat={cat.cat}
              style={{ cursor: 'pointer' }}
            >
              <div className="cat-icon">{cat.icon}</div>
              <h3>{cat.name}</h3>
              <p>{cat.desc}</p>
              <div className="cat-meta">
                <span className="cat-tag">{cat.count}</span>
                <span>{cat.level}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section" id="tutorials" style={{ paddingTop: 0 }}>
        <div className="section-header">
          <h2>Featured Tutorials</h2>
          <p>Curated picks across every category to get you started quickly.</p>
        </div>
        <div className="tutorial-list">
          {tutorials.map((tut, idx) => (
            <a 
              key={idx} 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                navigate(`/tutorial/${idx}?cat=${tut.cat}`);
              }}
              className="tut-row" 
              data-cat={tut.cat}
            >
              <div className="tut-dot"></div>
              <div className="tut-body">
                <h4>{tut.title}</h4>
                <p>{tut.desc}</p>
              </div>
              <div className="tut-meta">
                <span className="tut-level">{tut.level}</span>
                <span className="tut-time">⏱ {tut.time}</span>
              </div>
              <span className="tut-arrow">→</span>
            </a>
          ))}
        </div>
      </section>

      <div className="cta-banner" id="about">
        <h2>Ready to level up your <em>academic toolkit?</em></h2>
        <a href="#categories" className="btn-primary">Explore All Tutorials →</a>
      </div>

      <footer>
        <span>© 2026 <a href="https://flycodeinv.com">flycodeinv.com</a> · tutorial.flycodeinv.com</span>
        <div className="footer-links">
          <a href="#" onClick={() => navigate('/')}>Home</a>
          <a href="#categories">Categories</a>
          <a href="#" onClick={() => navigate('/contact')}>Contact</a>
          <a href="#">Privacy</a>
        </div>
      </footer>
    </>
  );
}
